import {
  Autocomplete,
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import TrafficClickIdentifier from "./TrafficClickIdentifier/TrafficClickIdentifier";
import Territory from "./Territory/Territory";
import Currency from "./Currency/Currency";
import Geo from "./Geo/Geo";
import Organization from "./Organization/Organization";
import TrafficSource from "./TrafficSource/TrafficSource";
import Industry from "./Industry/Industry";
import ProductType from "./ProductType/ProductType";
import DeviceType from "./DeviceType/DeviceType";
import FormatTypes from "./FormatTypes/FormatTypes";
import {
  resetSelectedMetaSearchSite,
  setGeoAssociatedTerritoryFilter,
  setGeoTimezoneFilter,
  setSelectedEntityType,
  setSelectedMetaSearchSite,
  setTerritoryLanguageFilter,
  setTrafficSourceFilter,
} from "../../../features/metaEntitiesSlice";
import { useDispatch } from "react-redux";
import CollectionMethods from "./CollectionMethods/CollectionMethods";
import CollectionTypes from "./CollectionTypes/CollectionTypes";
import { useNavigate, useSearchParams } from "react-router-dom";

interface MetaEntitiesProps {
  oauthTokenHeader: any;
}

const MetaEntities = ({ oauthTokenHeader }: MetaEntitiesProps) => {
  const navigate = useNavigate();
  const entityMap = {
    "Traffic Click Identifier": "traffic_click_identifier",
    Territory: "territory",
    Currency: "currency",
    Geo: "geo",
    Organization: "organization",
    "Traffic Source": "traffic_source",
    Industry: "industry",
    "Product Type": "product_type",
    "Device Type": "device_type",
    "Format Types": "format_types",
    "Collection Methods": "collection_methods",
    "Collection Types": "collection_types",
  };
  const reverseEntityMap: Record<string, string> = {};
  for (const [key, value] of Object.entries(entityMap)) {
    reverseEntityMap[value] = key;
  }
  const entityList = [
    { label: "Traffic Click Identifier" },
    { label: "Territory" },
    { label: "Currency" },
    { label: "Geo" },
    { label: "Organization" },
    { label: "Traffic Source" },
    { label: "Industry" },
    { label: "Product Type" },
    { label: "Device Type" },
    { label: "Format Types" },
    { label: "Collection Methods" },
    { label: "Collection Types" },
  ];

  const [entity, setEntity] = useState<string | null>(null);
  const [showMetaEntities, setShowMetaEntities] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse and handle URL query parameters on load
  useEffect(() => {
    const entityParam = searchParams.get("entity");
    if (entityParam) {
      const displayLabel = reverseEntityMap[entityParam];

      if (displayLabel) {
        const matchedEntity = entityList.find(
          (item) => item.label === displayLabel
        );

        if (matchedEntity) {
          setEntity(matchedEntity.label);
          setSelectedOption(matchedEntity);
          dispatch(setSelectedMetaSearchSite(matchedEntity.label));
          setShowMetaEntities(true);
          return;
        }
      }
      navigate("/admin/meta_entities", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dispatch, navigate]);
  const renderEntityComponent = () => {
    switch (entity) {
      case "Traffic Click Identifier":
        return <TrafficClickIdentifier oauthTokenHeader={oauthTokenHeader} />;
      case "Territory":
        return <Territory oauthTokenHeader={oauthTokenHeader} />;
      case "Currency":
        return <Currency oauthTokenHeader={oauthTokenHeader} />;
      case "Geo":
        return <Geo oauthTokenHeader={oauthTokenHeader} />;
      case "Organization":
        return <Organization oauthTokenHeader={oauthTokenHeader} />;
      case "Traffic Source":
        return <TrafficSource oauthTokenHeader={oauthTokenHeader} />;
      case "Industry":
        return <Industry oauthTokenHeader={oauthTokenHeader} />;
      case "Product Type":
        return <ProductType oauthTokenHeader={oauthTokenHeader} />;
      case "Device Type":
        return <DeviceType oauthTokenHeader={oauthTokenHeader} />;
      case "Format Types":
        return <FormatTypes />;
      case "Collection Methods":
        return <CollectionMethods />;
      case "Collection Types":
        return <CollectionTypes />;
      default:
        return null;
    }
  };

  const clearFilters = () => {
    setSearchParams({});
    dispatch(setSelectedEntityType(null));
    dispatch(resetSelectedMetaSearchSite());
    dispatch(setTrafficSourceFilter([]));
    dispatch(setTerritoryLanguageFilter([]));
    dispatch(setGeoAssociatedTerritoryFilter([]));
    dispatch(setGeoTimezoneFilter([]));
  };

  const handleSelectEntity = () => {
    if (entity) {
      // Get the URL-friendly value from the entityMap
      const urlValue = entityMap[entity as keyof typeof entityMap];

      // Update URL with query parameter using setSearchParams
      setSearchParams({ entity: urlValue });
      setShowMetaEntities(true);
    }
  };

  return (
    <>
      <Paper sx={{ padding: "8px 16px", mb: 2 }}>
        <Typography fontSize={24}>Meta Entities</Typography>
        <Typography sx={{ color: "#00000099", paddingBottom: "16px" }}>
          Create, Edit and Manage the system’s meta entities for a stremlined
          workflow
        </Typography>
        <Grid container mt={0} columnSpacing={3} mb={1}>
          <Grid item xs={10}>
            <Autocomplete
              value={selectedOption}
              sx={{ width: "100%" }}
              options={entityList}
              getOptionLabel={(option) => option.label}
              onChange={(_, newValue: any) => {
                clearFilters();
                setEntity(newValue?.label);
                setSelectedOption(newValue);
                dispatch(setSelectedMetaSearchSite(newValue?.label));
                dispatch(setSelectedEntityType(null));
                setShowMetaEntities(false);
              }}
              onInputChange={(_, __, reason) => {
                if (reason === "clear") {
                  setShowMetaEntities(false);
                  setSelectedOption(null);
                  setSearchParams({}); // Clear URL params when selection is cleared
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search entity type or choose from the list"
                  label="Select Entity Type"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                  }}
                  color="secondary"
                />
              )}
              size="small"
            />
          </Grid>
          <Grid item xs={2}>
            <Button
              variant="contained"
              data-tracker="232"
              onClick={handleSelectEntity}
              disabled={!entity}
              fullWidth
              size="large"
              color="secondary"
              sx={{ py: "7px" }}
            >
              Select Entity
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Dynamically render the selected component */}
      {showMetaEntities && renderEntityComponent()}
    </>
  );
};

export default MetaEntities;
